import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
// Importer le type User depuis le fichier centralisé
import { User, UserRoles } from '../types';

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;  // Kept for backward compatibility, but Logto handles refresh
  logtoId: string | null;  // Logto user ID for reference
  /** When set, the app acts as this user (super-admin impersonation). Not persisted. */
  impersonatedUser: User | null;
  isLoading: boolean;
  error: string | null;
  actions: {
    loginSuccess: (tokens: { access_token: string; refresh_token?: string }, user: User) => void;
    logout: () => void;
    setTokens: (tokens: { access_token: string; refresh_token?: string }) => void;
    setUser: (user: User | null) => void;
    updateUser: (updatedUserData: Partial<User>) => void;
    setLogtoId: (logtoId: string | null) => void;
    setLoading: (loading: boolean) => void;
    setError: (error: string | null) => void;
    clearError: () => void;
    startImpersonation: (user: User) => void;
    stopImpersonation: () => void;
  };
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      isAuthenticated: false,
      user: null,
      accessToken: null,
      refreshToken: null,
      logtoId: null,
      impersonatedUser: null,
      isLoading: false,
      error: null,
      actions: {
        loginSuccess: (tokens, user) => set({
          isAuthenticated: true,
          accessToken: tokens.access_token,
          refreshToken: tokens.refresh_token || null,
          user,
          impersonatedUser: null,
          isLoading: false,
          error: null
        }),
        logout: () => {
          // Allow the first-run onboarding to auto-launch again on the next login
          // (it only stays dismissed when the user opts out via "don't show again").
          try {
            sessionStorage.removeItem('welcome-autostart-done');
          } catch {
            /* sessionStorage unavailable — ignore */
          }
          set({
            isAuthenticated: false,
            user: null,
            accessToken: null,
            refreshToken: null,
            logtoId: null,
            impersonatedUser: null,
            error: null
          });
        },
        setTokens: (tokens) => set({
          accessToken: tokens.access_token,
          refreshToken: tokens.refresh_token || null
        }),
        setUser: (user) => set({ user }),
        // Implémentation de l'action updateUser
        updateUser: (updatedUserData) => set((state) => ({
          user: state.user ? { ...state.user, ...updatedUserData } : null
        })),
        setLogtoId: (logtoId) => set({ logtoId }),
        setLoading: (loading) => set({ isLoading: loading }),
        setError: (error) => set({ error, isLoading: false }),
        clearError: () => set({ error: null }),
        startImpersonation: (user) => set({ impersonatedUser: user }),
        stopImpersonation: () => set({ impersonatedUser: null }),
      },
    }),
    {
      name: 'auth-storage', // Nom de la clé dans localStorage
      storage: createJSONStorage(() => localStorage), // Utiliser localStorage
      partialize: (state) => ({
        // Persister uniquement ces champs
        // Note: With Logto, accessToken freshness is managed by Logto SDK
        // We persist the user info for quick UI rendering
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        logtoId: state.logtoId,
      }),
      onRehydrateStorage: () => {
        console.log("AuthStore: Hydration started");
        return (state) => {
          console.log("AuthStore: Hydration finished", state?.isAuthenticated);
          // Note: The actual auth state is managed by Logto
          // The persisted state is used for quick UI rendering
          // Logto will re-validate on page load
        };
      }
    }
  )
);

// Sélecteur pour accéder aux actions facilement
export const useAuthActions = () => useAuthStore((state) => state.actions);

/**
 * Returns true when the given user is a Super Admin (role = super_admin).
 */
export const selectIsSuperAdmin = (user: User | null | undefined): boolean =>
  user?.role === UserRoles.SuperAdmin;

/** React hook — resolves to true when the currently authenticated user is a super-admin (real user, not impersonated). */
export const useIsSuperAdmin = () =>
  useAuthStore((state) => selectIsSuperAdmin(state.user));

/** Effective user for display and permission checks: impersonated user when impersonating, else real user. */
export const useEffectiveUser = () =>
  useAuthStore((state) => state.impersonatedUser ?? state.user);

/** Impersonation state for navbar banner and "Stop impersonating". */
export const useImpersonation = () =>
  useAuthStore(
    useShallow((state) => ({
      impersonatedUser: state.impersonatedUser,
      isImpersonating: state.impersonatedUser != null,
      realUser: state.user,
      startImpersonation: state.actions.startImpersonation,
      stopImpersonation: state.actions.stopImpersonation,
    }))
  );

/** Returns true when the given user is an organization admin or super-admin.
 * Mirrors the backend `get_current_active_organization_admin` dependency:
 *   - role === UserRoles.Admin, OR
 *   - role === UserRoles.SuperAdmin
 */
export const selectIsOrganizationAdmin = (user: User | null | undefined): boolean =>
  user?.role === UserRoles.Admin || selectIsSuperAdmin(user);

/** React hook — resolves to true when the effective user (impersonated or real) is an org admin or super-admin. */
export const useIsOrganizationAdmin = () =>
  useAuthStore((state) => selectIsOrganizationAdmin(state.impersonatedUser ?? state.user));
